const VIEWPORT_EDGE_PX = 80;
const CONTAINER_EDGE_PX = 72;
const MAX_SCROLL_SPEED = 16;

export const resolveDragAutoScrollDelta = (
  clientY: number,
  top: number,
  bottom: number,
  edgePx: number,
  maxSpeed = MAX_SCROLL_SPEED,
) => {
  const availableHeight = Math.max(0, bottom - top);
  const safeEdgePx = Math.min(edgePx, Math.max(16, availableHeight / 3));
  const topZone = top + safeEdgePx;
  const bottomZone = bottom - safeEdgePx;

  if (clientY < topZone) {
    const intensity = Math.min(1, (topZone - clientY) / safeEdgePx);
    return -Math.max(1, Math.round(maxSpeed * intensity));
  }

  if (clientY > bottomZone) {
    const intensity = Math.min(1, (clientY - bottomZone) / safeEdgePx);
    return Math.max(1, Math.round(maxSpeed * intensity));
  }

  return 0;
};

const isRootScroller = (element: HTMLElement) => (
  element === document.documentElement || element === document.body
);

const isVerticallyScrollable = (element: HTMLElement) => {
  const overflowY = window.getComputedStyle(element).overflowY;

  if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') {
    return false;
  }

  return element.scrollHeight > element.clientHeight + 1;
};

const collectScrollContainers = (clientX: number, clientY: number) => {
  const containers: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();
  let node = document.elementFromPoint(clientX, clientY) as HTMLElement | null;

  while (node) {
    if (node instanceof HTMLElement && !isRootScroller(node) && isVerticallyScrollable(node) && !seen.has(node)) {
      containers.push(node);
      seen.add(node);
    }

    node = node.parentElement;
  }

  return containers;
};

const applyScrollDelta = (element: HTMLElement | Window, delta: number) => {
  if (!delta) {
    return;
  }

  if (element === window) {
    window.scrollBy(0, delta);
    return;
  }

  (element as HTMLElement).scrollTop += delta;
};

let activeCount = 0;
let pointerX = 0;
let pointerY = 0;
let hasPointer = false;
let animationFrame = 0;
let isStopped = true;

const stopLoop = () => {
  isStopped = true;
  activeCount = 0;
  hasPointer = false;

  if (animationFrame) {
    window.cancelAnimationFrame(animationFrame);
    animationFrame = 0;
  }

  document.removeEventListener('dragover', handlePointerMove, true);
  document.removeEventListener('drag', handlePointerMove, true);
  document.removeEventListener('dragend', handleNativeDragEnd, true);
  document.removeEventListener('drop', handleNativeDragEnd, true);
};

const handleNativeDragEnd = () => {
  stopLoop();
};

const tick = () => {
  if (isStopped || activeCount <= 0 || !hasPointer) {
    animationFrame = 0;
    return;
  }

  const viewportDelta = resolveDragAutoScrollDelta(
    pointerY,
    0,
    window.innerHeight,
    VIEWPORT_EDGE_PX,
  );

  applyScrollDelta(window, viewportDelta);

  const probeY = pointerY < VIEWPORT_EDGE_PX
    ? VIEWPORT_EDGE_PX + 8
    : (pointerY > window.innerHeight - VIEWPORT_EDGE_PX
      ? window.innerHeight - VIEWPORT_EDGE_PX - 8
      : pointerY);

  collectScrollContainers(pointerX, probeY).forEach((container) => {
    const rect = container.getBoundingClientRect();
    const delta = resolveDragAutoScrollDelta(
      pointerY,
      rect.top,
      rect.bottom,
      CONTAINER_EDGE_PX,
    );

    applyScrollDelta(container, delta);
  });

  animationFrame = window.requestAnimationFrame(tick);
};

const handlePointerMove = (event: DragEvent) => {
  if (isStopped) {
    return;
  }

  pointerX = event.clientX;
  pointerY = event.clientY;
  hasPointer = true;

  if (activeCount > 0 && !animationFrame) {
    animationFrame = window.requestAnimationFrame(tick);
  }
};

export const getLastTrainingDragPointer = () => ({ x: pointerX, y: pointerY });

export const beginTrainingDragAutoScroll = () => {
  if (!isStopped && activeCount > 0) {
    return;
  }

  isStopped = false;
  activeCount = 1;
  document.addEventListener('dragover', handlePointerMove, true);
  document.addEventListener('drag', handlePointerMove, true);
  document.addEventListener('dragend', handleNativeDragEnd, true);
  document.addEventListener('drop', handleNativeDragEnd, true);
};

export const endTrainingDragAutoScroll = () => {
  stopLoop();
};
